/**
 * useTransactionTypes Hooks
 *
 * React Query hooks for TransactionType CRUD operations with automatic caching
 * and cache invalidation.
 *
 * Hooks provided:
 * - useTransactionTypes: List transaction_types with optional filters
 * - useTransactionType: Get single transaction_type by ID
 * - useTransactionTypesDropdown: Get transaction_types formatted for dropdown selection
 * - useCreateTransactionType: Create new transaction_type
 * - useUpdateTransactionType: Update existing transaction_type
 * - useDeleteTransactionType: Delete transaction_type
 *
 * Features:
 * - Automatic caching with configurable stale/gc times
 * - Cache invalidation on mutations
 * - Loading and error states
 * - Request deduplication
 * - Retry with exponential backoff
 *
 * Generated by CodeGen OS v1.0.0
 * Generated at: 2026-04-25T14:23:22.162974Z
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  UseQueryOptions,
  UseQueryResult,
  UseMutationResult,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import { transaction_typeService } from '../services/transaction_type.service';
import { transaction_typeKeys } from './transaction_typeKeys';
import type {
  TransactionType,
  TransactionTypeCreateRequest,
  TransactionTypeUpdateRequest,
  TransactionTypeFilterOptions,
} from '../types/transaction_type.types';

// ---- CODAIC:CUSTOM IMPORTS ----
// Add custom imports below. This section is preserved across regeneration.
// ---- CODAIC:END CUSTOM ----

/**
 * Cache timing constants (in milliseconds)
 * Configurable via entity definition or defaults
 */
const TRANSACTION_TYPE_STALE_TIME = 300000; // 5 minutes
const TRANSACTION_TYPE_GC_TIME = 1800000; // 30 minutes

/**
 * Retry configuration
 * - 3 retries with exponential backoff
 * - Delays: 1s, 2s, 4s (2^attemptIndex * 1000)
 */
const RETRY_COUNT = 3;
const retryDelay = (attemptIndex: number) =>
  Math.min(1000 * 2 ** attemptIndex, 30000);

/**
 * Options for the useTransactionTypes hook
 */
export interface UseTransactionTypesOptions {
  /**
   * Filter to only return active transaction_types
   * @default true
   */
  activeOnly?: boolean;

  /**
   * Additional filter options for the query
   */
  filters?: Omit<TransactionTypeFilterOptions, 'is_active'>;

  /**
   * Whether to enable the query
   * @default true
   */
  enabled?: boolean;
}

/**
 * Return type for the useTransactionTypes hook
 */
export interface UseTransactionTypesResult
  extends Omit<UseQueryResult<TransactionType[], Error>, 'data'> {
  /** Array of transaction_type data */
  data: TransactionType[] | undefined;

  /** Convenience alias for data */
  transactionTypes: TransactionType[] | undefined;
}

/**
 * Hook to fetch transaction_types with React Query caching
 *
 * Provides transaction_type data with optimized caching:
 * - staleTime: 5 minutes - Data is fresh for this period
 * - gcTime: 30 minutes - Unused cache entries are collected after this period
 *
 * @param options - Hook options including active filter and additional filters
 * @returns Query result with transaction_type data, loading, error states, and refetch function
 *
 * @example
 * ```typescript
 * import { useTransactionTypes } from './hooks/useTransactionTypes';
 *
 * function TransactionTypeList() {
 *   const { data, isLoading, error, refetch } = useTransactionTypes();
 *
 *   if (isLoading) return <Skeleton />;
 *   if (error) return <Error message={error.message} onRetry={refetch} />;
 *
 *   return (
 *     <List>
 *       {data?.map(item => <ListItem key={item.id}>{item.code}</ListItem>)}
 *     </List>
 *   );
 * }
 *
 * // With filters
 * const { data } = useTransactionTypes({ activeOnly: false, filters: { search: 'test' } });
 * ```
 */
export function useTransactionTypes(
  options: UseTransactionTypesOptions = {}
): UseTransactionTypesResult {
  const { activeOnly = true, filters = {}, enabled = true } = options;

  // Build the filter options combining activeOnly with other filters
  const filterOptions: TransactionTypeFilterOptions = {
    ...filters,
    is_active: activeOnly ? true : undefined,
  };

  const queryResult = useQuery<TransactionType[], Error>({
    queryKey: transaction_typeKeys.list(filterOptions),
    queryFn: async () => {
      const items = await transaction_typeService.getAll(filterOptions);
      return items;
    },
    staleTime: TRANSACTION_TYPE_STALE_TIME,
    gcTime: TRANSACTION_TYPE_GC_TIME,
    enabled,
    retry: RETRY_COUNT,
    retryDelay,
  });

  return {
    ...queryResult,
    transactionTypes: queryResult.data,
  };
}

/**
 * Hook to fetch a single transaction_type by ID
 *
 * @param id - TransactionType ID (UUID)
 * @param options - Optional React Query options
 * @returns Query result with transaction_type data
 *
 * @example
 * ```typescript
 * const { data: transactionType, isLoading } = useTransactionType('uuid-here');
 * ```
 */
export function useTransactionType(
  id: string,
  options?: Omit<UseQueryOptions<TransactionType | null, Error>, 'queryKey' | 'queryFn'>
): UseQueryResult<TransactionType | null, Error> {
  return useQuery<TransactionType | null, Error>({
    queryKey: transaction_typeKeys.detail(id),
    queryFn: () => transaction_typeService.getById(id),
    staleTime: TRANSACTION_TYPE_STALE_TIME,
    gcTime: TRANSACTION_TYPE_GC_TIME,
    enabled: !!id,
    retry: RETRY_COUNT,
    retryDelay,
    ...options,
  });
}

/**
 * Dropdown option type for transaction_type select components
 */
export interface TransactionTypeDropdownOption {
  value: string;
  label: string;
  data: TransactionType;
}

/**
 * Hook to fetch transaction_types formatted for dropdown selection
 *
 * Returns memoized options with { value, label, data } structure.
 *
 * @param activeOnly - Only include active transaction_types (default: true)
 * @returns Query result with dropdown options
 *
 * @example
 * ```typescript
 * const { data: options, isLoading } = useTransactionTypesDropdown();
 *
 * <Select options={options} isLoading={isLoading} />
 * ```
 */
export function useTransactionTypesDropdown(activeOnly: boolean = true) {
  const queryResult = useQuery<TransactionTypeDropdownOption[], Error>({
    queryKey: transaction_typeKeys.dropdown(activeOnly),
    queryFn: () => transaction_typeService.getForDropdown(!activeOnly),
    staleTime: TRANSACTION_TYPE_STALE_TIME,
    gcTime: TRANSACTION_TYPE_GC_TIME,
    retry: RETRY_COUNT,
    retryDelay,
  });

  // Memoize the options transformation
  const options = useMemo(() => queryResult.data ?? [], [queryResult.data]);

  return {
    ...queryResult,
    options,
  };
}

/**
 * Hook to create a new transaction_type
 *
 * Automatically invalidates transaction_type list queries on success.
 *
 * @returns Mutation result with mutate function
 *
 * @example
 * ```typescript
 * const { mutate: createTransactionType, isPending } = useCreateTransactionType();
 *
 * createTransactionType({
 *   code: 'New TransactionType',
 *   // ... other fields
 * });
 * ```
 */
export function useCreateTransactionType(): UseMutationResult<
  TransactionType,
  Error,
  TransactionTypeCreateRequest,
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation<TransactionType, Error, TransactionTypeCreateRequest>({
    mutationFn: (data: TransactionTypeCreateRequest) =>
      transaction_typeService.create(data),
    onSuccess: () => {
      // Invalidate all transaction_type list queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: transaction_typeKeys.all });
    },
  });
}

/**
 * Hook to update an existing transaction_type
 *
 * Automatically invalidates transaction_type list and detail queries on success.
 *
 * @returns Mutation result with mutate function
 *
 * @example
 * ```typescript
 * const { mutate: updateTransactionType } = useUpdateTransactionType();
 *
 * updateTransactionType({
 *   id: 'uuid-here',
 *   data: { code: 'Updated Name' }
 * });
 * ```
 */
export function useUpdateTransactionType(): UseMutationResult<
  TransactionType,
  Error,
  { id: string; data: TransactionTypeUpdateRequest },
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation<
    TransactionType,
    Error,
    { id: string; data: TransactionTypeUpdateRequest }
  >({
    mutationFn: ({ id, data }) => transaction_typeService.update(id, data),
    onSuccess: (_, { id }) => {
      // Invalidate all transaction_type list queries
      queryClient.invalidateQueries({ queryKey: transaction_typeKeys.all });
      // Invalidate the specific transaction_type detail query
      queryClient.invalidateQueries({ queryKey: transaction_typeKeys.detail(id) });
    },
  });
}

/**
 * Hook to delete a transaction_type
 *
 * Automatically invalidates transaction_type queries on success.
 *
 * @returns Mutation result with mutate function
 *
 * @example
 * ```typescript
 * const { mutate: deleteTransactionType, isPending } = useDeleteTransactionType();
 *
 * deleteTransactionType('uuid-here');
 * ```
 */
export function useDeleteTransactionType(): UseMutationResult<void, Error, string, unknown> {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (id: string) => transaction_typeService.delete(id),
    onSuccess: (_, id) => {
      // Invalidate all transaction_type queries
      queryClient.invalidateQueries({ queryKey: transaction_typeKeys.all });
      // Remove the specific detail query from cache
      queryClient.removeQueries({ queryKey: transaction_typeKeys.detail(id) });
    },
  });
}

/**
 * Hook to deactivate a transaction_type (soft delete)
 *
 * Automatically invalidates transaction_type queries on success.
 *
 * @returns Mutation result with mutate function
 *
 * @example
 * ```typescript
 * const { mutate: deactivateTransactionType } = useDeactivateTransactionType();
 *
 * deactivateTransactionType('uuid-here');
 * ```
 */
export function useDeactivateTransactionType(): UseMutationResult<
  TransactionType,
  Error,
  string,
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation<TransactionType, Error, string>({
    mutationFn: (id: string) =>
      transaction_typeService.update(id, { is_active: false } as TransactionTypeUpdateRequest),
    onSuccess: (_, id) => {
      // Invalidate all transaction_type queries
      queryClient.invalidateQueries({ queryKey: transaction_typeKeys.all });
      // Invalidate the specific detail query
      queryClient.invalidateQueries({ queryKey: transaction_typeKeys.detail(id) });
    },
  });
}

// ---- CODAIC:CUSTOM HOOKS ----
// Add custom hooks below. This section is preserved across regeneration.
// ---- CODAIC:END CUSTOM ----

// Default export for convenience
export default useTransactionTypes;
